// The one content region a saved-views list reveals (design-system §5, spec
// 16 §4 Loading, D-305 ruling 21). The chip row's height is decided by the
// views read, so the switcher row and the table are revealed together, once
// the views read and the first list page have both settled, behind one
// reserving skeleton. The reveal latches: once shown, later reads (a filter
// change, a refetch) render inside the table's own loading state and never
// bring the skeleton back, so nothing painted moves afterwards.

import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Skeleton, TableSkeleton } from "../../../../components/states";

export interface ViewsRegionProps {
  /** `views.settled && <the list's first page has settled>`. */
  readonly ready: boolean;
  readonly children: ReactNode;
  /** Skeleton rows reserved for the table. */
  readonly rows?: number;
  readonly cols?: number;
}

export function ViewsRegion({ ready, children, rows = 8, cols = 5 }: ViewsRegionProps) {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState(ready);
  if (ready && !revealed) setRevealed(true);
  if (!revealed) {
    return (
      <div data-testid="views-region-loading" aria-busy="true">
        <div className="ds-viewswitcher" aria-hidden="true">
          <Skeleton width="3rem" height="26px" />
          <Skeleton width="6rem" height="26px" />
        </div>
        <TableSkeleton rows={rows} cols={cols} label={t("views.region.loading")} />
      </div>
    );
  }
  return <>{children}</>;
}
